import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import { entitlementsCatalog, entitlementsAdmin } from '../../urls/api-shipments/entitlements';

const TIER_OPTIONS = [
  { value: 'free', label: 'Free' },
  { value: 'founding', label: 'Founding (grandfathered)' },
  { value: 'pro', label: 'Pro' },
  { value: 'enterprise', label: 'Enterprise' }
];

const CATEGORY_ORDER = ['premium', 'enterprise', 'legacy_core'];
const CATEGORY_LABELS = { premium: 'Pro features', enterprise: 'Enterprise features', legacy_core: 'Included (free)' };

// Monthly list price + copy per tier. Mirrors api-shipments TIER_PRICING
// (free/founding $0, pro $99, enterprise $299) and the Bill chat upsell.
const TIER_META = {
  free: { label: 'Free', price: 0, blurb: 'Core booking & live tracking' },
  founding: { label: 'Founding', price: 0, blurb: 'Grandfathered \u2014 Pro features at $0', grandfathered: true },
  pro: { label: 'Pro', price: 99, blurb: 'All premium features unlocked' },
  enterprise: { label: 'Enterprise', price: 299, blurb: 'Multi-location + dedicated manager' }
};
const TIER_CARD_ORDER = ['free', 'founding', 'pro', 'enterprise'];
// Rank for upgrade/downgrade direction; founding unlocks premium like Pro.
const TIER_RANK = { free: 0, founding: 1, pro: 2, enterprise: 3 };
const TIER_FEATURE_RANK = { free: 0, founding: 2, pro: 2, enterprise: 3 };
const formatTierPrice = price => (price > 0 ? '$' + price + '/mo' : '$0');

const payloadOf = res => (res && res.data && res.data.payload) || (res && res.data) || {};

// Admin-only panel in the customer manager: set a store's subscription tier,
// trial window, and per-feature tri-state overrides (Inherit / On / Off).
const StoreEntitlementsPanel = ({ storeId, authToken }) => {
  const [catalog, setCatalog] = useState(null);
  const [tier, setTier] = useState('founding');
  const [trialEndsAt, setTrialEndsAt] = useState('');
  const [overrides, setOverrides] = useState({});
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');

  const headers = { Authorization: 'Bearer ' + (authToken || ''), 'content-type': 'application/json' };

  const load = useCallback(() => {
    setLoading(true);
    setNotice('');
    Promise.all([axios.get(entitlementsCatalog, { headers }), axios.get(entitlementsAdmin(storeId), { headers })])
      .then(([cat, adm]) => {
        const catPayload = payloadOf(cat);
        const admPayload = payloadOf(adm);
        const rec = admPayload.record;
        setCatalog(catPayload);
        setTier(rec ? rec.subscription_tier : 'founding');
        setTrialEndsAt(rec && rec.trial_ends_at ? String(rec.trial_ends_at).slice(0, 10) : '');
        setOverrides(rec && rec.feature_overrides ? rec.feature_overrides : {});
        setResolved(admPayload.resolved);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setNotice('Unable to load plan settings.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, authToken]);

  useEffect(() => {
    if (storeId) load();
  }, [storeId, load]);

  // Simple on/off override per feature; clearing returns to the plan default.
  const setOverride = (key, on) => {
    setOverrides(prev => ({ ...prev, [key]: on ? 'on' : 'off' }));
  };
  const clearOverride = key => {
    setOverrides(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const startTrial = () => setTrialEndsAt(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));

  const save = () => {
    setSaving(true);
    setNotice('');
    axios
      .patch(
        entitlementsAdmin(storeId),
        {
          subscription_tier: tier,
          trial_ends_at: trialEndsAt ? new Date(trialEndsAt + 'T23:59:59').toISOString() : null,
          feature_overrides: overrides
        },
        { headers }
      )
      .then(res => {
        const p = payloadOf(res);
        setResolved(p.resolved);
        if (p.record) setOverrides(p.record.feature_overrides || {});
        setNotice('Saved.');
        setSaving(false);
      })
      .catch(() => {
        setNotice('Save failed.');
        setSaving(false);
      });
  };

  // Change the store's plan straight from a card (keeps trial + overrides).
  const applyTier = newTier => {
    if (saving || newTier === tier) return;
    setSaving(true);
    setNotice('');
    axios
      .patch(
        entitlementsAdmin(storeId),
        {
          subscription_tier: newTier,
          trial_ends_at: trialEndsAt ? new Date(trialEndsAt + 'T23:59:59').toISOString() : null,
          feature_overrides: overrides
        },
        { headers }
      )
      .then(res => {
        const p = payloadOf(res);
        setResolved(p.resolved);
        setTier(p.record ? p.record.subscription_tier : newTier);
        if (p.record) setOverrides(p.record.feature_overrides || {});
        setNotice('Plan changed to ' + (TIER_META[newTier] ? TIER_META[newTier].label : newTier) + '.');
        setSaving(false);
      })
      .catch(() => {
        setNotice('Plan change failed.');
        setSaving(false);
      });
  };

  // Top feature labels a tier unlocks, for the plan-card body.
  const tierHighlights = (tierValue, catalogRef) => {
    if (!catalogRef || !catalogRef.features) return [];
    const rank = TIER_FEATURE_RANK[tierValue] != null ? TIER_FEATURE_RANK[tierValue] : 0;
    return Object.keys(catalogRef.features)
      .map(key => catalogRef.features[key])
      .filter(f => (f.min_tier ? TIER_FEATURE_RANK[f.min_tier] || 0 : 0) <= rank)
      .map(f => f.label);
  };

  const header = (
    <div className="store-detail-card__header section">
      <h4>Plan &amp; Features</h4>
      <p>Set this store&apos;s subscription tier, trial, and per-feature overrides.</p>
    </div>
  );

  if (loading) {
    return (
      <div className="store-detail-card">
        {header}
        <div style={{ padding: 16 }}>Loading…</div>
      </div>
    );
  }
  if (!catalog) {
    return (
      <div className="store-detail-card">
        {header}
        <div style={{ padding: 16 }}>{notice || 'Unavailable.'}</div>
      </div>
    );
  }

  const byCategory = { premium: [], enterprise: [], legacy_core: [] };
  Object.entries(catalog.features).forEach(([key, f]) => {
    (byCategory[f.category] || (byCategory[f.category] = [])).push([key, f]);
  });

  const currentRank = TIER_RANK[tier] != null ? TIER_RANK[tier] : 0;
  const currentMeta = TIER_META[tier] || { label: tier, price: 0 };

  return (
    <div className="store-detail-card">
      {header}
      <div style={{ padding: '0 16px 16px' }}>
        <div style={{ fontSize: 13, marginBottom: 10 }}>
          Current plan:{' '}
          <strong>{currentMeta.label}</strong>{' '}
          <span style={{ opacity: 0.7 }}>({formatTierPrice(currentMeta.price)})</span>
          {trialEndsAt ? <span style={{ marginLeft: 8, color: '#f7941d' }}>Trial ends {trialEndsAt}</span> : null}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 16 }}>
          {TIER_CARD_ORDER.filter(t => TIER_META[t]).map(t => {
            const meta = TIER_META[t];
            const isCurrent = t === tier;
            const rank = TIER_RANK[t];
            const direction = rank > currentRank ? 'Upgrade' : 'Downgrade';
            const highlights = tierHighlights(t, catalog).slice(0, 5);
            return (
              <div
                key={t}
                style={{
                  border: isCurrent ? '2px solid #f7941d' : '1px solid rgba(128,128,128,.25)',
                  borderRadius: 12,
                  padding: 14,
                  display: 'flex',
                  flexDirection: 'column',
                  background: isCurrent ? 'rgba(247,148,29,.06)' : 'transparent'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>{meta.label}</span>
                  {isCurrent ? <span style={{ fontSize: 11, color: '#f7941d', fontWeight: 700 }}>CURRENT</span> : null}
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, margin: '4px 0 2px' }}>{formatTierPrice(meta.price)}</div>
                <div style={{ fontSize: 12, opacity: 0.7, minHeight: 32 }}>{meta.blurb}</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 10px', fontSize: 12, flex: '1 1 auto' }}>
                  {highlights.map(label => (
                    <li key={label} style={{ padding: '2px 0', opacity: 0.9 }}>
                      <span style={{ color: '#4caf50', marginRight: 6 }}>&#10003;</span>
                      {label}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className={isCurrent ? 'icon-edit-button' : 'create-merchant-btn'}
                  disabled={isCurrent || saving}
                  onClick={() => applyTier(t)}
                  style={{ width: '100%', opacity: isCurrent ? 0.6 : 1 }}
                >
                  {isCurrent ? 'Current plan' : saving ? 'Working\u2026' : direction + ' to ' + meta.label}
                </button>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
          <label className="detail-select-group">
            <span>Subscription Tier</span>
            <select className="detail-select" value={tier} onChange={e => setTier(e.target.value)}>
              {TIER_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="detail-select-group">
            <span>Trial ends</span>
            <input className="detail-input" type="date" value={trialEndsAt} onChange={e => setTrialEndsAt(e.target.value)} />
          </label>
          <button type="button" className="icon-edit-button" onClick={startTrial}>
            Start 14-day trial
          </button>
          {trialEndsAt ? (
            <button type="button" className="icon-edit-button" onClick={() => setTrialEndsAt('')}>
              Clear trial
            </button>
          ) : null}
        </div>

        {CATEGORY_ORDER.map(cat => (
          <div key={cat} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, textTransform: 'uppercase', opacity: 0.7, margin: '8px 0 4px' }}>{CATEGORY_LABELS[cat]}</div>
            {(byCategory[cat] || []).map(([key, f]) => {
              const ov = overrides[key];
              const effective = resolved && resolved.features ? resolved.features[key] : false;
              const isOn = ov === 'on' ? true : ov === 'off' ? false : effective;
              const overridden = ov === 'on' || ov === 'off';
              return (
                <div
                  key={key}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid rgba(128,128,128,.18)' }}
                >
                  <span>{f.label}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {overridden ? (
                      <button
                        type="button"
                        onClick={() => clearOverride(key)}
                        title="Reset to plan default"
                        style={{ background: 'transparent', border: 'none', color: '#9aa0a6', cursor: 'pointer', fontSize: 12 }}
                      >
                        reset
                      </button>
                    ) : null}
                    <div
                      role="switch"
                      aria-checked={isOn}
                      onClick={() => setOverride(key, !isOn)}
                      style={{ width: 42, height: 24, borderRadius: 12, background: isOn ? '#4caf50' : '#9aa0a6', position: 'relative', cursor: 'pointer', transition: 'background .15s', flex: '0 0 auto' }}
                    >
                      <div style={{ position: 'absolute', top: 2, left: isOn ? 20 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
          <button type="button" className="create-merchant-btn" disabled={saving} onClick={save}>
            {saving ? 'Saving…' : 'Save plan'}
          </button>
          {notice ? <span style={{ fontSize: 13 }}>{notice}</span> : null}
          <span style={{ fontSize: 12, opacity: 0.6 }}>Toggle each feature on or off; “reset” returns it to the plan default.</span>
        </div>
      </div>
    </div>
  );
};

export default StoreEntitlementsPanel;
